"use client";

import { DelegationChain, DelegationIdentity } from "@icp-sdk/core/identity";
import Decimal from "decimal.js";
import type React from "react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import { createLogAdapter } from "@/lib/logger/console-adapter";
import { generateNonce } from "@/lib/utils/nonce-generator";
import { useIdentity } from "./IdentityContext";
import { useLimitOrder } from "./LimitOrderContext";

const uiLog = createLogAdapter("contexts/TakeProfitStopLossContext.tsx");

// 类型定义
interface TpSlOrder {
	id: string;
	user_principal: string;
	token_id: string;
	status: "active" | "paused" | "completed" | "error" | "cancelled";
	parameters: {
		trade_record: {
			trade_id: string;
			token_id: string;
			buy_price: string;
			buy_amount: string;
			executed_at: string;
		};
		take_profit: {
			enabled: boolean;
			price_ratio: number;
			amount_ratio: number;
			trigger_price: string;
			sell_amount: string;
		};
		stop_loss: {
			enabled: boolean;
			price_ratio: number;
			amount_ratio: number;
			trigger_price: string;
			sell_amount: string;
		};
		execution_status: {
			take_profit_executed: boolean;
			take_profit_executed_at?: string;
			stop_loss_executed: boolean;
			stop_loss_executed_at?: string;
		};
		settings?: {
			slippage: number;
			expires_at?: string;
		};
	};
	created_at: string;
	updated_at: string;
}

interface CreateTpSlParams {
	trade_id: string;
	token_id: string;
	buy_price: string; // 1e11精度
	buy_amount: string; // 买入数量
	take_profit: {
		enabled: boolean;
		price_ratio: number;
		amount_ratio: number;
	};
	stop_loss: {
		enabled: boolean;
		price_ratio: number;
		amount_ratio: number;
	};
	slippage?: number;
}

interface TakeProfitStopLossContextType {
	orders: TpSlOrder[];
	loading: boolean;
	error: string | null;

	// 操作方法
	createTpSlOrder: (params: CreateTpSlParams) => Promise<boolean>;
	cancelTpSlOrder: (orderId: string) => Promise<boolean>;
	pauseTpSlOrder: (orderId: string) => Promise<boolean>;
	resumeTpSlOrder: (orderId: string) => Promise<boolean>;

	// 查询方法
	fetchOrders: (tokenId?: string) => Promise<void>;
	refreshOrders: () => Promise<void>;

	// 凭证管理（复用限价挂单的凭证）
	hasValidCredentials: boolean;
	credentialsExpireAt: string | null;
}

const TakeProfitStopLossContext = createContext<
	TakeProfitStopLossContextType | undefined
>(undefined);

export function TakeProfitStopLossProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const { identity, isAuthenticated, principal } = useIdentity();
	const { hasValidCredentials, credentialsExpireAt } = useLimitOrder();

	// 状态管理
	const [orders, setOrders] = useState<TpSlOrder[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// 生成唯一的交易ID
	const generateTradeId = useCallback((): string => {
		const timestamp = Date.now();
		const random = Math.random().toString(36).substring(2, 15);
		const principalShort = principal ? principal.substring(0, 8) : "unknown";
		return `trade_${timestamp}_${principalShort}_${random}`;
	}, [principal]);

	// 生成签名数据
	const generateSignature = useCallback(
		async (action: string, parameters: any) => {
			if (!identity) throw new Error("Identity not available");

			const payload = {
				action,
				parameters,
				timestamp: Date.now(),
				nonce: generateNonce(),
			};

			const payloadString = JSON.stringify(payload);
			const payloadBytes = new TextEncoder().encode(payloadString);
			const signature = await identity.sign(payloadBytes);

			// 获取委托链
			const delegation = (identity as any).getDelegation();
			const delegationChain = delegation ? delegation.toJSON() : null;

			// 确保签名是正确的格式
			const signatureArray =
				signature instanceof Uint8Array ? signature : new Uint8Array(signature);

			return {
				payloadBase64: Buffer.from(payloadString).toString("base64"),
				signatureBase64: Buffer.from(signatureArray).toString("base64"),
				delegationBase64: delegationChain
					? Buffer.from(JSON.stringify(delegationChain)).toString("base64")
					: "",
			};
		},
		[identity],
	);

	// 创建止盈止损订单
	const createTpSlOrder = useCallback(
		async (params: CreateTpSlParams): Promise<boolean> => {
			if (!isAuthenticated || !identity || !principal) {
				setError("请先登录");
				return false;
			}

			if (!hasValidCredentials) {
				setError("请先授权自动化交易");
				return false;
			}

			setLoading(true);
			setError(null);

			try {
				// 验证输入数据
				if (!params.buy_price || !params.buy_amount) {
					throw new Error("买入价格和数量不能为空");
				}

				const buyPriceNum = parseFloat(params.buy_price);
				const buyAmountNum = parseFloat(params.buy_amount);

				if (
					isNaN(buyPriceNum) ||
					isNaN(buyAmountNum) ||
					buyPriceNum <= 0 ||
					buyAmountNum <= 0
				) {
					throw new Error("买入价格和数量必须为有效的正数");
				}

				// 检查是否至少启用了止盈或止损中的一个
				if (!params.take_profit.enabled && !params.stop_loss.enabled) {
					throw new Error("至少需要启用止盈或止损中的一个");
				}

				// 构建请求参数（匹配API Schema）
				const requestParams = {
					trade_id: params.trade_id,
					token_id: params.token_id,
					buy_price: params.buy_price,
					buy_amount: params.buy_amount,
					take_profit: {
						enabled: params.take_profit.enabled,
						price_ratio: params.take_profit.price_ratio,
						amount_ratio: params.take_profit.amount_ratio,
					},
					stop_loss: {
						enabled: params.stop_loss.enabled,
						price_ratio: params.stop_loss.price_ratio,
						amount_ratio: params.stop_loss.amount_ratio,
					},
					slippage: params.slippage || 1,
				};

				// 生成签名
				const signatureData = await generateSignature(
					"CREATE_TP_SL_ORDER",
					requestParams,
				);

				// 发送请求
				const response = await fetch("/api/automation/tp-sl/create", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						"X-Auth-Payload": signatureData.payloadBase64,
						"X-Auth-Signature": signatureData.signatureBase64,
						"X-Auth-Delegation": signatureData.delegationBase64,
					},
					body: JSON.stringify(requestParams),
				});

				const result = await response.json();

				if (!response.ok) {
					throw new Error(result.error || "创建止盈止损订单失败");
				}

				// 刷新订单列表
				await fetchOrders(params.token_id);

				return true;
			} catch (error: any) {
				uiLog.error("创建止盈止损订单失败:", error);
				setError(error.message || "创建止盈止损订单失败");
				return false;
			} finally {
				setLoading(false);
			}
		},
		[
			isAuthenticated,
			identity,
			principal,
			hasValidCredentials,
			generateSignature,
		],
	);

	// 取消止盈止损订单
	const cancelTpSlOrder = useCallback(
		async (orderId: string): Promise<boolean> => {
			if (!isAuthenticated || !identity || !principal) {
				setError("请先登录");
				return false;
			}

			setLoading(true);
			setError(null);

			try {
				const signatureData = await generateSignature("CANCEL_TP_SL_ORDER", {
					order_id: orderId,
				});

				const response = await fetch(`/api/automation/tp-sl/${orderId}`, {
					method: "DELETE",
					headers: {
						"X-Auth-Payload": signatureData.payloadBase64,
						"X-Auth-Signature": signatureData.signatureBase64,
						"X-Auth-Delegation": signatureData.delegationBase64,
					},
				});

				if (!response.ok) {
					const result = await response.json();
					throw new Error(result.error || "取消订单失败");
				}

				// 更新本地状态
				setOrders((prev) =>
					prev.map((order) =>
						order.id === orderId
							? { ...order, status: "cancelled" as const }
							: order,
					),
				);

				return true;
			} catch (error: any) {
				uiLog.error("取消订单失败:", error);
				setError(error.message || "取消订单失败");
				return false;
			} finally {
				setLoading(false);
			}
		},
		[isAuthenticated, identity, principal, generateSignature],
	);

	// 暂停止盈止损订单
	const pauseTpSlOrder = useCallback(
		async (orderId: string): Promise<boolean> => {
			if (!isAuthenticated || !identity || !principal) {
				setError("请先登录");
				return false;
			}

			setLoading(true);
			setError(null);

			try {
				const signatureData = await generateSignature("PAUSE_TP_SL_ORDER", {
					order_id: orderId,
				});

				const response = await fetch(
					`/api/automation/tp-sl/${orderId}/status`,
					{
						method: "PUT",
						headers: {
							"Content-Type": "application/json",
							"X-Auth-Payload": signatureData.payloadBase64,
							"X-Auth-Signature": signatureData.signatureBase64,
							"X-Auth-Delegation": signatureData.delegationBase64,
						},
						body: JSON.stringify({ status: "paused" }),
					},
				);

				if (!response.ok) {
					const result = await response.json();
					throw new Error(result.error || "暂停订单失败");
				}

				// 更新本地状态
				setOrders((prev) =>
					prev.map((order) =>
						order.id === orderId
							? { ...order, status: "paused" as const }
							: order,
					),
				);

				return true;
			} catch (error: any) {
				uiLog.error("暂停订单失败:", error);
				setError(error.message || "暂停订单失败");
				return false;
			} finally {
				setLoading(false);
			}
		},
		[isAuthenticated, identity, principal, generateSignature],
	);

	// 恢复止盈止损订单
	const resumeTpSlOrder = useCallback(
		async (orderId: string): Promise<boolean> => {
			if (!isAuthenticated || !identity || !principal) {
				setError("请先登录");
				return false;
			}

			setLoading(true);
			setError(null);

			try {
				const signatureData = await generateSignature("RESUME_TP_SL_ORDER", {
					order_id: orderId,
				});

				const response = await fetch(
					`/api/automation/tp-sl/${orderId}/status`,
					{
						method: "PUT",
						headers: {
							"Content-Type": "application/json",
							"X-Auth-Payload": signatureData.payloadBase64,
							"X-Auth-Signature": signatureData.signatureBase64,
							"X-Auth-Delegation": signatureData.delegationBase64,
						},
						body: JSON.stringify({ status: "active" }),
					},
				);

				if (!response.ok) {
					const result = await response.json();
					throw new Error(result.error || "恢复订单失败");
				}

				// 更新本地状态
				setOrders((prev) =>
					prev.map((order) =>
						order.id === orderId
							? { ...order, status: "active" as const }
							: order,
					),
				);

				return true;
			} catch (error: any) {
				uiLog.error("恢复订单失败:", error);
				setError(error.message || "恢复订单失败");
				return false;
			} finally {
				setLoading(false);
			}
		},
		[isAuthenticated, identity, principal, generateSignature],
	);

	// 获取订单列表
	const fetchOrders = useCallback(
		async (tokenId?: string) => {
			if (!isAuthenticated || !principal) {
				setOrders([]);
				return;
			}

			setLoading(true);
			setError(null);

			try {
				const params = new URLSearchParams();
				if (tokenId) params.append("token_id", tokenId);

				const response = await fetch(`/api/automation/tp-sl/orders?${params}`, {
					headers: {
						"X-User-Principal": principal,
					},
				});

				if (!response.ok) {
					throw new Error("获取订单列表失败");
				}

				const data = await response.json();
				setOrders(data.orders || []);
			} catch (error: any) {
				uiLog.error("获取订单列表失败:", error);
				setError(error.message || "获取订单列表失败");
				setOrders([]);
			} finally {
				setLoading(false);
			}
		},
		[isAuthenticated, principal],
	);

	// 刷新订单
	const refreshOrders = useCallback(async () => {
		await fetchOrders();
	}, [fetchOrders]);

	// 初始化
	useEffect(() => {
		if (isAuthenticated && principal && hasValidCredentials) {
			fetchOrders();
		} else {
			setOrders([]);
		}
	}, [isAuthenticated, principal, hasValidCredentials]);

	// 定期刷新订单状态（每30秒）
	useEffect(() => {
		if (!isAuthenticated || !hasValidCredentials) return;

		const interval = setInterval(() => {
			fetchOrders();
		}, 30000);

		return () => clearInterval(interval);
	}, [isAuthenticated, hasValidCredentials, fetchOrders]);

	const value: TakeProfitStopLossContextType = {
		orders,
		loading,
		error,
		createTpSlOrder,
		cancelTpSlOrder,
		pauseTpSlOrder,
		resumeTpSlOrder,
		fetchOrders,
		refreshOrders,
		hasValidCredentials,
		credentialsExpireAt,
	};

	return (
		<TakeProfitStopLossContext.Provider value={value}>
			{children}
		</TakeProfitStopLossContext.Provider>
	);
}

// Hook to use the context
export function useTakeProfitStopLoss() {
	const context = useContext(TakeProfitStopLossContext);
	if (context === undefined) {
		throw new Error(
			"useTakeProfitStopLoss must be used within a TakeProfitStopLossProvider",
		);
	}
	return context;
}
